import { useDictionary } from '@eih-netbroker/common';
import { useCallback } from 'react';
import { ReactComponent as DepositIcon } from '../../../../assets/icons/add-line.svg';
import { ReactComponent as AllocationIcon } from '../../../../assets/icons/arrow-right-up-line-v2.svg';
import { ReactComponent as TransferIcon } from '../../../../assets/icons/transfer.svg';
import { useDialog } from '../../../../context/DialogContext';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { NbButton } from '../../../../shared/components/button/Button';
import { useAccountBalance } from '../../../../shared/services/useAccountBalance';
import { useAccounts } from '../../../../shared/services/useAccounts';
import { CashAllocationModal } from '../../../cash-action/components/allocation/CashAllocationModal';
import { CashDepositModal } from '../../../cash-action/components/deposit/CashDepositModal';
import { CashTransferModal } from '../../../cash-action/components/transfer/CashTransferModal';
import { usePayment } from '../../../cash-action/services/usePayment';
import classes from './CashPopoverContent.module.scss';
import emptyClasses from './CashPopoverEmptyContent.module.scss';

type CashPopoverEmptyContentProps = {
  handleCashClose: () => void;
};

export const CashPopoverEmptyContent = ({ handleCashClose }: CashPopoverEmptyContentProps) => {
  const { translation } = useLocale();
  const { selectedClientName, selectedAccountOrClient } = useAccounts();
  const { doesClientHaveCashInventory } = useAccountBalance();
  const { formatDictionaryType } = useDictionary();
  const { openDialog } = useDialog();
  const { handleCashActionDialogOpen } = usePayment();

  const openDepositDialog = useCallback(async () => {
    await openDialog((close: () => void) => <CashDepositModal onClose={close} />);
  }, [openDialog]);

  const openTransferDialog = useCallback(async () => {
    await openDialog((close: () => void) => <CashTransferModal onClose={close} />);
  }, [openDialog]);

  const openAllocationDialog = useCallback(async () => {
    await openDialog((close: () => void) => <CashAllocationModal onClose={close} />);
  }, [openDialog]);

  return (
    <div className={classes.cash}>
      <div className={classes.cashHeader}>
        <div className={classes.cashHeaderText}>
          <h4 data-hj-suppress>{selectedClientName}</h4>
          <p className={classes.cashHeaderUser}>
            {selectedAccountOrClient?.account?.accountCategory
              ? formatDictionaryType(selectedAccountOrClient.account.accountCategory, 'accountCategoryTypes')
              : translation.header.allAccounts}
          </p>
        </div>
      </div>
      <div className={emptyClasses.empty}>
        <p className={emptyClasses.emptyTitle}>{translation.header.cashEmpty.noCashTitle}</p>
        <div className={emptyClasses.emptyText}>
          {doesClientHaveCashInventory ? (
            <p>{translation.header.cashEmpty.noCashDepositTransferText}</p>
          ) : (
            <p>{translation.header.cashEmpty.noCashDepositOnlyText}</p>
          )}
        </div>
        <div className={emptyClasses.emptyActions}>
          <NbButton
            data-testid="cash-popover-empty-content-deposit-button"
            size="small"
            color="primary"
            variant="contained"
            startIcon={<DepositIcon />}
            className={emptyClasses.emptyActionButtonPrimary}
            onClick={() => handleCashActionDialogOpen(openDepositDialog, handleCashClose)}
          >
            {translation.header.cash.deposit}
          </NbButton>
          {doesClientHaveCashInventory && (
            <>
              <NbButton
                data-testid="cash-popover-empty-content-transfer-button"
                size="small"
                color="primary"
                variant="outlined"
                startIcon={<TransferIcon />}
                className={emptyClasses.emptyActionButtonOutline}
                onClick={() => handleCashActionDialogOpen(openTransferDialog, handleCashClose)}
              >
                {translation.header.cash.transfer}
              </NbButton>
              <NbButton
                data-testid="cash-popover-empty-content-allocation-button"
                size="small"
                color="primary"
                variant="outlined"
                startIcon={<AllocationIcon />}
                className={emptyClasses.emptyActionButtonOutline}
                onClick={() => handleCashActionDialogOpen(openAllocationDialog, handleCashClose)}
              >
                {translation.header.cash.allocation}
              </NbButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
